import { MovableDocument } from "./documentMoveValidation";

const LICENSE_DOCUMENT_TYPES = new Set(["2", "13"]);

const hasDocumentId = (value: any) => {
  const normalized = String(value ?? "").trim();
  return normalized !== "" && normalized !== "0" && normalized.toLowerCase() !== "null";
};

/**
 * The ID section is rendered from an identity record (saveIdentityInfo) that
 * references the front/back application documents. automovedocument relocates
 * the documents but leaves those references behind, so the files keep showing
 * in the ID section after a move. This builds the saveIdentityInfo payload
 * that clears the references for the moved slots (mirroring the payload shape
 * ProofOfIdForm submits). Returns null when the identity record references
 * none of the moved documents.
 */
export const buildIdentityCleanupPayload = (
  identificationData: Record<string, any> | null | undefined,
  movedDocuments: Array<Pick<MovableDocument, "id">>,
  trnId: string | number
) => {
  if (!identificationData) return null;

  const movedIds = new Set(movedDocuments.map((document) => String(document.id)));
  const frontId = identificationData.application_document_id;
  const backId = identificationData.BarcodeDocumentID;
  const frontMoved = hasDocumentId(frontId) && movedIds.has(String(frontId));
  const backMoved = hasDocumentId(backId) && movedIds.has(String(backId));
  if (!frontMoved && !backMoved) return null;

  const keptFrontId = !frontMoved && hasDocumentId(frontId) ? frontId : null;
  const keptBackId = !backMoved && hasDocumentId(backId) ? backId : null;
  const allFilesGone = keptFrontId == null && keptBackId == null;
  const documentTypeId = String(identificationData.documenttype_id ?? "");

  const ocr_data: Record<string, any> = allFilesGone
    ? {
        first_name: null,
        middle_name: null,
        last_name: null,
        dob: null,
        issue_date: null,
        expiration_date: null,
        doc_state_type: null,
        no_doc_reason: null,
        no_doc_flag: false,
      }
    : {
        first_name: identificationData.first_name ?? null,
        middle_name: identificationData.middle_name ?? null,
        last_name: identificationData.last_name ?? null,
        dob: identificationData.DOB ?? null,
        issue_date: identificationData.issue_date ?? null,
        expiration_date: identificationData.expiration_date ?? null,
        doc_state_type: null,
        no_doc_reason: null,
        no_doc_flag: false,
      };

  if (LICENSE_DOCUMENT_TYPES.has(documentTypeId)) {
    if (allFilesGone) {
      Object.assign(ocr_data, {
        address: null,
        state: null,
        postal_code: null,
        city: null,
        license_id: null,
        barcode_document_id: null,
      });
    } else {
      Object.assign(ocr_data, {
        address: identificationData.street_address ?? null,
        state: identificationData.state ?? null,
        postal_code: identificationData.zipcode ?? null,
        city: identificationData.city ?? null,
        license_id: identificationData.license_id ?? null,
        barcode_document_id: keptBackId,
      });
    }
  } else if (!allFilesGone) {
    if (documentTypeId === "1") {
      ocr_data.passport_no = identificationData.passport_no ?? null;
    }
    if (documentTypeId === "10") {
      ocr_data.uscis_number = identificationData.uscis_number ?? null;
    }
    if (documentTypeId === "11") {
      ocr_data.controller_number = identificationData.controller_number ?? null;
    }
  }

  return {
    ID: identificationData.ID,
    document_id: identificationData.documenttype_id,
    SOCRStatus: null,
    application_id: identificationData.application_id,
    subscriber_id: identificationData.subscriber_id,
    applicant_id: identificationData.applicant_id,
    SOCRComment: null,
    files: { application_document_id: keptFrontId },
    back_files: { application_document_id: keptBackId },
    noDocReason: null,
    ocr_data,
    subUrl: "/saveIdentityInfo",
    trn_id: trnId,
    availableToUpload_id: "Yes",
  };
};
